import { useCallback, useEffect, useState } from 'react';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  BenefitType,
  IOffersFragment,
  PromotionType,
  SwapType,
  useLoyaltyUserOffersLazyQuery,
} from 'generated/graphql-gateway';
import { IAppliedOffer, ISwap } from 'generated/rbi-graphql';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { parseStringifiedJSON } from 'utils/parse-string';

import { ICartEntryAdapter } from './types';

const getAppliedOffersFromStorage = () => {
  const storedAppliedOffers = LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_OFFERS);
  return parseStringifiedJSON({ value: storedAppliedOffers, defaultValue: [] }) || [];
};

const updateAppliedOffersInStorage = (appliedOffers: IAppliedOffer[]) => {
  LocalStorage.setItem(StorageKeys.APPLIED_LOYALTY_OFFERS, JSON.stringify(appliedOffers));
};

export const useLoyaltyOffers = (loyaltyId = '') => {
  const [upsizeAvailable, setUpsizeAvailable] = useState(false);
  const [offers, setOffers] = useState<IOffersFragment[]>([]);
  const [appliedOffers, setAppliedOffers] = useState(getAppliedOffersFromStorage);
  const [queryOffers, { data }] = useLoyaltyUserOffersLazyQuery({ fetchPolicy: 'network-only' });

  useEffect(() => {
    const userOffers = data?.loyaltyUser?.offers;
    if (userOffers?.length) {
      const upsize = userOffers.some(offer =>
        offer?.benefits?.some(benefits => benefits.type === BenefitType.SWAP)
      );
      setUpsizeAvailable(upsize);
      setOffers(userOffers as IOffersFragment[]);
    }
  }, [data, setOffers, setUpsizeAvailable]);

  const parseEntry = useCallback(
    ({ _id, cartId, price = 0, quantity = 1, children }: ICartEntry) => {
      const parsed: ICartEntryAdapter = {
        sanityId: _id,
        lineId: cartId,
        price,
        quantity,
      };

      if (children.length) {
        parsed.children = children.map(parseEntry);
      }

      return parsed;
    },
    []
  );

  const parseOffers = useCallback(
    (appliedLoyaltyOffers: IAppliedOffer[]) =>
      appliedLoyaltyOffers.map(({ cartId: lineId, id, swap }) => ({
        ...(swap && { swap: parseSwap(swap) }),
        id,
        lineId,
        type: PromotionType.OFFER,
      })),
    []
  );

  const parseSwap = ({ cartId: lineId, to, from, swapType }: ISwap) => ({
    to,
    from,
    lineId,
    type: swapType as SwapType,
  });

  const getLoyaltyOffers = useCallback(
    async (cartEntries: ICartEntry[]) => {
      queryOffers({
        variables: {
          loyaltyId,
          where: {
            appliedPromotions: parseOffers(appliedOffers),
            cartEntries: cartEntries.map(parseEntry),
          },
        },
      });
    },
    [appliedOffers, loyaltyId, queryOffers, parseEntry, parseOffers]
  );

  const resetUpsizeAvailability = () => setUpsizeAvailable(false);

  const resetAppliedOffers = () => {
    updateAppliedOffersInStorage([]);
    setAppliedOffers([]);
  };

  const updateAppliedOffers = (cartEntry: ICartEntry) => {
    setAppliedOffers((oldOffers: IAppliedOffer[]) => {
      const updatedOffers = oldOffers.filter(offer => offer?.swap?.cartId !== cartEntry?.cartId);
      updateAppliedOffersInStorage(updatedOffers);

      return updatedOffers;
    });
  };

  const applyOffer = (newOffer: IAppliedOffer) => {
    setAppliedOffers((oldOffers: IAppliedOffer[]) => {
      const updatedOffers = oldOffers.concat(newOffer);
      updateAppliedOffersInStorage(updatedOffers);

      return updatedOffers;
    });
  };

  return {
    appliedOffers,
    getLoyaltyOffers,
    offers,
    resetUpsizeAvailability,
    setAppliedOffers,
    upsizeAvailable,
    applyOffer,
    updateAppliedOffers,
    resetAppliedOffers,
  };
};
